const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (newToken: string) => {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
};

const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem('refreshToken');
  
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await fetch(`${API_BASE}/api/auth/refresh/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      localStorage.setItem('accessToken', data.access);
      if (data.refresh) {
        localStorage.setItem('refreshToken', data.refresh);
      }
      return data.access;
    } else {
      clearAuthData();
      return null;
    }
  } catch (error) {
    console.error('Token refresh error:', error);
    clearAuthData();
    return null;
  }
};

const clearAuthData = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  
  window.dispatchEvent(new CustomEvent('auth:logout'));
};

export const apiFetch = async (
  url: string,
  options: RequestInit = {},
  requireAuth: boolean = true
): Promise<Response> => {
  const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
  
  const headers: HeadersInit = {
    ...options.headers,
  };

  if (requireAuth) {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
    }
  }

  if (!(options.body instanceof FormData) && !('Content-Type' in headers)) {
    (headers as Record<string, string>)['Content-Type'] = 'application/json';
  }

  let response = await fetch(fullUrl, {
    ...options,
    headers,
  });

  if (response.status === 401 && requireAuth) {
    if (!isRefreshing) {
      isRefreshing = true;
      
      const newToken = await refreshAccessToken();
      
      isRefreshing = false;
      
      if (newToken) {
        onTokenRefreshed(newToken);
        
        (headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(fullUrl, {
          ...options,
          headers,
        });
      } else {
        clearAuthData();
      }
    } else {
      const newToken = await new Promise<string>((resolve) => {
        subscribeTokenRefresh(resolve);
      });
      
      (headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(fullUrl, {
        ...options,
        headers,
      });
    }
  }

  return response;
};

export const api = {
  get: (url: string, requireAuth = true) => 
    apiFetch(url, { method: 'GET' }, requireAuth),
  
  post: (url: string, data?: any, requireAuth = true) =>
    apiFetch(url, {
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
    }, requireAuth),
  
  put: (url: string, data?: any, requireAuth = true) =>
    apiFetch(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, requireAuth),
  
  patch: (url: string, data?: any, requireAuth = true) =>
    apiFetch(url, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }, requireAuth),
  
  delete: (url: string, requireAuth = true) =>
    apiFetch(url, { method: 'DELETE' }, requireAuth),
};

export default api;